////////LIBRARY/////////
import React, { useEffect, useState } from 'react'
import { useSelector, shallowEqual } from 'react-redux'
import { cloneDeep, find, map, keys, get, isUndefined, reject, includes } from 'lodash'
import PropTypes from 'prop-types'
import styled from 'styled-components'
///////COMPONENTS///////
import UseWindowResize from '../../hooks/UseWindowResize'
import useListSort from '../Table/hooks/UseListSort'
/////////ASSETS/////////
import ArrowUp from '../../assets/icones/global/arrow-up-big.svg'
import ArrowDown from '../../assets/icones/global/arrow-down-big.svg'
import ArrowUpGrey from '../../assets/icones/global/arrow-up-big-grey.svg'
import ArrowDownGrey from '../../assets/icones/global/arrow-down-big-grey.svg'

/////////STYLED/////////
const ListSearchableWrapper = styled.div`
	position: relative;
	font-size: ${(props) => props.theme.font.fs};
`
const ListSearchableHeader = styled.div`
	position: relative;
	border-bottom: 1px solid ${(props) => props.theme.colors.listNav.border};
	padding: 15px 15px 15px 30px;
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
`
const ListSearchableItems = styled.div`
	position: relative;
	display:flex;
	flex-direction:column;
	overflow-y: scroll;
	height: ${(props) => `${props.sizeHeightActions ? props.sizeHeight - props.sizeHeightActions : props.sizeHeight}px`};
	&::-webkit-scrollbar {
		width: 15px;
		background-color:rgba(0,0,0,.15);
		height: 50px;
	}
	  &::-webkit-scrollbar-thumb {
		  background: #4D4F5C; 
	}
	  &::-webkit-scrollbar-thumb:active {
		background rgba(77, 79, 92, 0.7); 
	}
	  &::-webkit-scrollbar-button:vertical:decrement:single-button {
		  background: no-repeat url(${ArrowUp}) center;
		  height:15px;
	}
	  &::-webkit-scrollbar-button:vertical:increment:single-button {
		  background: no-repeat url(${ArrowDown}) center;
		  height:15px;
	}
	&::-webkit-scrollbar-button:vertical:decrement:single-button:active {
		background: no-repeat url(${ArrowUpGrey}) center;
		height:15px;
  }
	&::-webkit-scrollbar-button:vertical:increment:single-button:active {
		background: no-repeat url(${ArrowDownGrey}) center;
		height:15px;
  }
	  /* scrollbar styling standard version */
	  & {
		scrollbar-color: #4D4F5C #E5E5E5;
		scrollbar-width: thin;
	}
`
const ListSearchableCheckAllWrapper = styled.div`
	display: flex;
	align-items: center;
	margin-right: 10px;
`
const ListSearchableItem = styled.div`
	padding: 10px 30px;
	display: flex;
	align-items: center;
	& > .separateLabel {
		line-height: 18px;
		padding: 10px 0px;
	}
	& > .separateLabel:last-child {
		margin-left: auto;
		flex-basis: 50%;
		padding-left: 10px;
		border-left: 1px solid ${(props) => props.theme.colors.TabNav.border};
	}
`
const ListSearchableInput = styled.input`
	margin: 0px 19px 0px 0px;
`
const ListSearchableCheckAllLabel = styled.label`
	font-weight: bold;
`
const ListSearchableLabel = styled.label``
const ListSearchableLabelCounter = styled.span`
	color: ${(props) => props.theme.colors.primary};
	margin-left: 5px;
`
const RowsSelectedCounter = styled.span`
	font-weight: bold;
	color: ${(props) => props.theme.colors.primary};
`
const ListSearchableSummary = styled.a`
	font-weight: bold;
	color: ${(props) => props.theme.colors.primary};
	text-decoration: underline;
	cursor: pointer;
`
/////////STYLED/////////

const ListSearchable = ({
	inputColumns,
	inputData,
	hookList,
	inputConfig,
	remoteCheckAll,
	remoteCheckAllFunc,
	summaryHandler,
	summaryList = [],
	sizeHeightActions,
	readOnly = false,
	sortColumnName,
	sortDir,
	forceReduxData = false, // When true, base data will be always be what is stored => check/uncheck all cannot modify this list
	fixedBaseHeight, // In modal mode, we need base height to be fixed, not set by window height
}) => {
	const { translations } = useSelector(
		(state) => ({
			translations: state.languages.translations,
		}),
		shallowEqual
	)

	const [tableData, trigger, setTrigger, setTableData, sortDirection, setSortDirection] = useListSort(inputColumns, inputData)

	///////////////////////////////// HANDLE ///////////////////////////////////////

	const handleSortTableHeader = (headerName) => {
		setTrigger({
			data: headerName,
			trigger: trigger + 1,
		})
	}

	const [rowsSelected, setRowsSelected] = useState([])
	const [useReduxData, setUseReduxData] = useState(true)
	const [checkedAll, setCheckedAll] = useState(!isUndefined(remoteCheckAll) ? remoteCheckAll : false)

	const handleRowSelected = (selectedId, isChecked) => {
		let newRowsSelected = cloneDeep(summaryList)
		// find() was used before, but seems to not work with ids ?
		const rowFound = !!includes(summaryList, selectedId)

		if (!rowFound && !isChecked) {
			newRowsSelected.push(selectedId)
		}
		if (rowFound && isChecked) {
			newRowsSelected = reject(newRowsSelected, (o) => o === selectedId)
			checkedAll && setCheckedAll(false)
		}
		forceReduxData ? setUseReduxData(true) : setUseReduxData(false)
		hookList(newRowsSelected)
	}

	const handleCheckAll = (status) => {
		let newRowsSelected = []
		if (status) {
			map(inputData, (row, _id) => newRowsSelected.push(row.id))
		}
		// Might need it if we want to loose checked items selection
		//setUseReduxData(false)
		forceReduxData ? setUseReduxData(true) : setUseReduxData(false)
		setCheckedAll(status)
		if (!isUndefined) remoteCheckAllFunc(status)
		hookList(newRowsSelected)
	}

	const [dimensions] = UseWindowResize()

	/////////////////////////////// USE EFFECT /////////////////////////////////////

	// Make sure that we reset checked list on load
	useEffect(() => {
		// We clear selected values on mounting, instead of updating
		hookList([])
		setRowsSelected([])
	}, [])

	// Filters actions
	useEffect(() => {
		// handleCheckAll(checkedAll)
		if (!isUndefined(sortDir) && !isUndefined(sortColumnName)) {
			setSortDirection(sortDir)
			setTableData(cloneDeep(inputData))
			handleSortTableHeader(sortColumnName)
		} else {
			setTableData(cloneDeep(inputData))
		}
	}, [inputData])

	useEffect(() => {
		if (!isUndefined(sortDir) && !isUndefined(sortColumnName)) {
			setSortDirection(sortDir)
			handleSortTableHeader(sortColumnName)
		}
	}, [sortColumnName, sortDir])

	///////////////////////////////// RENDER ///////////////////////////////////////

	return (
		<ListSearchableWrapper>
			{!inputConfig.noStandardFilters && (
				<ListSearchableHeader>
					<ListSearchableCheckAllWrapper>
						<ListSearchableInput
							type="checkbox"
							disabled={readOnly}
							checked={checkedAll}
							onClick={() => {
								if (!readOnly) handleCheckAll(!checkedAll)
							}}
						/>
						<ListSearchableCheckAllLabel>{translations['page__roleManagement__roleSheet_allChecked']}</ListSearchableCheckAllLabel>
					</ListSearchableCheckAllWrapper>
					{!isUndefined(inputConfig.labelSelected) && (
						<div>
							<RowsSelectedCounter>{!isUndefined(summaryList) ? summaryList.length : 0}</RowsSelectedCounter>{' '}
							{inputConfig.labelSelected}{' '}
							{!isUndefined(summaryHandler) && (
								<ListSearchableSummary onClick={() => summaryHandler()}>{inputConfig.textLinkSummary}</ListSearchableSummary>
							)}
						</div>
					)}
				</ListSearchableHeader>
			)}
			<ListSearchableItems sizeHeight={fixedBaseHeight ? fixedBaseHeight : dimensions.height - 80} sizeHeightActions={sizeHeightActions}>
				{tableData &&
					map(tableData, (row, _id) => {
						let isRowSelected = false
						let row_id_name
						if (summaryList?.length > 0)
							isRowSelected = find(summaryList, (o) => {
								return o === row.id
							})
								? true
								: false
						return (
							<ListSearchableItem lineselected={summaryList?.length > 0 && isRowSelected}>
								{
									// Map with columns
									map(inputColumns, (column, _columnid) => {
										const row_name = find(keys(row), (o) => o === column.dataHook)
										let ret
										// If item was selected
										const wasSelected = row.selected || row.isSelected

										if (column.isCheckboxValue) {
											row_id_name = row_name
											if (wasSelected && useReduxData) {
												const r = find(summaryList, (o) => o === get(row, row_name))
												if (isUndefined(r)) summaryList.push(row.id)
											}
											let f = find(summaryList, (o) => o === row.id)
											f =
												!isUndefined(f) &&
												(isUndefined(summaryList) || (!isUndefined(summaryList) && includes(summaryList, f)))
											ret = (
												<ListSearchableInput
													name="id"
													type="checkbox"
													disabled={row.selected || readOnly}
													value={get(row, row_name)}
													checked={f}
													onClick={() => {
														if (!readOnly) {
															handleRowSelected(get(row, row_name), isRowSelected)
														}
													}}
												></ListSearchableInput>
											)
										}
										if (column.isLabel) {
											let addSymbol = ''
											if (
												!isUndefined(column.dataCounter) &&
												get(row, column.dataCounter.column) > 0 &&
												!isUndefined(column.dataCounter.addSymbol)
											)
												addSymbol = column.dataCounter.addSymbol
											ret = column.alreadyTranslated ? (
												<ListSearchableLabel className={column.className}>
													{get(row, row_name)}
													{!isUndefined(column.dataCounter) ? (
														column.dataCounter.haveTag ? (
															get(row, column.dataCounter.column) > 0 ? (
																<ListSearchableLabelCounter>
																	{`(${addSymbol}${get(row, column.dataCounter.column)} ${
																		column.dataCounter.keyword
																	}${get(row, column.dataCounter.column) > 1 ? 's' : ''})`}
																</ListSearchableLabelCounter>
															) : (
																''
															)
														) : (
															<ListSearchableLabelCounter>
																{`(${addSymbol}${get(row, column.dataCounter.column)} ${column.dataCounter.keyword}${
																	get(row, column.dataCounter.column) > 1 ? 's' : ''
																})`}
															</ListSearchableLabelCounter>
														)
													) : (
														''
													)}
												</ListSearchableLabel>
											) : (
												<ListSearchableLabel className={column.className}>
													{translations[get(row, row_name)]}
													{!isUndefined(column.dataCounter) ? (
														<ListSearchableLabelCounter>
															{`(${addSymbol}${get(row, column.dataCounter.column)} ${column.dataCounter.keyword}${
																get(row, column.dataCounter.column) > 1 ? 's' : ''
															})`}
														</ListSearchableLabelCounter>
													) : (
														''
													)}
												</ListSearchableLabel>
											)
										}
										return ret
									})
								}
							</ListSearchableItem>
						)
					})}
			</ListSearchableItems>
		</ListSearchableWrapper>
	)
}

ListSearchable.propTypes = {
	inputColumns: PropTypes.array.isRequired,
	inputData: PropTypes.array,
	inputConfig: PropTypes.object.isRequired,
}
ListSearchable.defaultPropTypes = {
	inputColumns: [],
	inputData: [],
	inputConfig: {},
}

export default ListSearchable
